import { useEffect, useRef, useState } from "react";
import { oklchToCss, type OklchColor } from "@/document/model/color";

export function ColorSlider({
  label,
  value,
  min,
  max,
  background,
  onChange,
  onChangeEnd,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  background: string;
  onChange: (value: number) => void;
  onChangeEnd?: (value: number) => void;
}) {
  const latestValueRef = useRef(value);
  const draggingRef = useRef(false);
  const [localValue, setLocalValue] = useState<number | undefined>(undefined);
  const displayValue = localValue ?? value;
  const position = (displayValue - min) / Math.max(0.0001, max - min);

  useEffect(() => {
    latestValueRef.current = value;
    if (!draggingRef.current) {
      setLocalValue(undefined);
    }
  }, [value]);

  function pick(event: React.PointerEvent<HTMLDivElement>): number {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const nextValue = min + x * (max - min);
    latestValueRef.current = nextValue;
    setLocalValue(nextValue);
    onChange(nextValue);
    return nextValue;
  }

  return (
    <label className="grid grid-cols-[44px_1fr_38px] items-center gap-2 text-[11px] text-slate-500">
      <span>{label}</span>
      <div
        className="relative h-3 cursor-pointer overflow-hidden rounded-full border border-black/40"
        style={{ background }}
        onPointerDown={event => {
          draggingRef.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          pick(event);
        }}
        onPointerMove={event => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            pick(event);
          }
        }}
        onPointerUp={event => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
          draggingRef.current = false;
          setLocalValue(undefined);
          onChangeEnd?.(latestValueRef.current);
        }}
        onPointerCancel={event => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
          draggingRef.current = false;
          setLocalValue(undefined);
          onChangeEnd?.(latestValueRef.current);
        }}
      >
        <div
          className="pointer-events-none absolute top-1/2 size-4 rounded-full"
          style={{
            left: `${position * 100}%`,
            transform: "translate(-50%, -50%)",
            boxShadow: "0 0 0 0.5px #fff, 0 0 0 1px #000",
          }}
        />
      </div>
      <span className="text-right tabular-nums">{Math.round(displayValue)}</span>
    </label>
  );
}

export function hueSliderBackground(): string {
  return "linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)";
}

export function opacitySliderBackground(color: OklchColor): string {
  const transparent = oklchToCss({ ...color, alpha: 0 });
  const opaque = oklchToCss({ ...color, alpha: 1 });
  return [
    `linear-gradient(to right, ${transparent}, ${opaque})`,
    "repeating-conic-gradient(#94a3b8 0% 25%, #e2e8f0 0% 50%) 50% / 8px 8px",
  ].join(", ");
}
